import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';

import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import { combineLatest } from 'rxjs/observable/combineLatest';
import { tap } from 'rxjs/operators';

import { chartConfig } from '../../../../app.constants';
import { CircleChartData } from '../../../../../models/chart';
import { ChannelReach } from '../../../../../models/influencer/channel-reach';
import { UserService } from '../../../../services/user.service';
import { ChannelReachItem } from './channel-reach-item/chanel-reach-item';

interface ChannelItem {
    channelId: number;
    channelName: string;
    platformName: string;
}

@Component({
    selector: 'channel-reach',
    templateUrl: './channel-reach.component.html',
    styleUrls: ['./channel-reach.component.scss']
})
export class ChannelReachComponent implements OnInit, OnDestroy {
    private _initSubject: Subject<boolean>;
    private _changeSubscription: Subscription;

    private readonly _placeholderChannelReachItems: ChannelReachItem[];
    private readonly _selectedColor: string;
    private readonly _unselectedColor: string;

    public hasNoData = true;
    public channelReachItems: ChannelReachItem[];

    private _channelReachesSubject: Subject<ChannelReach[]>;
    private _channelReaches: ChannelReach[] = [];
    @Input() set channelReaches(value: ChannelReach[]) {
        this._channelReaches = value;
        this._channelReachesSubject.next(value);
    }

    private _selectedChannelIdSubject: Subject<number>;
    private _selectedChannelId: number;
    @Input() set selectedChannelId(value: number) {
        this._selectedChannelId = value;
        this._selectedChannelIdSubject.next(value);
    }

    @Input() forPayingUser: boolean;
    @Output() exceedMaxChannelSelection: EventEmitter<any>;
    @Input() isFetchingData: boolean;

    public showChannelSelector = false;
    public channelSource: ChannelItem[] = [];
    public otherSelectedChannelIds: number[] = [];
    private readonly _maxSelectedItemsCount = 3;

    constructor(private _userService: UserService, cd: ChangeDetectorRef) {
        this._selectedColor = _userService.chartColors[0];
        this._unselectedColor = chartConfig.unselectedColors[0];
        this.exceedMaxChannelSelection = new EventEmitter<any>();
        this._placeholderChannelReachItems = [
            {
                channelName: 'Instagram',
                placeholderMode: true,
                chartData: [{
                    value: 0,
                    lineColor: 'rgba(113, 125, 141, 0.5)'
                }],
                uniqueCode: `-111_false`
            },
            {
                channelName: 'Blogs',
                placeholderMode: true,
                chartData: [{
                    value: 0,
                    lineColor: 'rgba(113, 125, 141, 0.5)'
                }],
                uniqueCode: `-222_false`
            },
            {
                channelName: 'Pinterest',
                placeholderMode: true,
                chartData: [{
                    value: 0,
                    lineColor: 'rgba(113, 125, 141, 0.5)'
                }],
                uniqueCode: `-333_false`
            },
            {
                channelName: 'YouTube',
                placeholderMode: true,
                chartData: [{
                    value: 0,
                    lineColor: 'rgba(113, 125, 141, 0.5)'
                }],
                uniqueCode: `-444_false`
            }
        ];

        this._initSubject = new Subject<boolean>();
        this._channelReachesSubject = new Subject<ChannelReach[]>();
        this._selectedChannelIdSubject = new Subject<number>();

        this._changeSubscription = combineLatest([
            this._channelReachesSubject,
            this._selectedChannelIdSubject,
            this._initSubject
        ]).pipe(
            tap(() => {
                this.showChannelSelector = false;
                cd.detectChanges();
            })
            ).subscribe({
                next: ([channelReaches, selectedChannelId]) => {
                    if (this.shouldShowPlaceholder(channelReaches, selectedChannelId)) {
                        this.channelReachItems = this._placeholderChannelReachItems;
                        this.hasNoData = true;
                        return;
                    }

                    this.hasNoData = false;
                    this.calculateChannelSelector(channelReaches, selectedChannelId);
                    this.drawChannelReaches(channelReaches, selectedChannelId, this.otherSelectedChannelIds);
                }
            });
    }

    ngOnDestroy(): void {
        this._changeSubscription.unsubscribe();
    }

    ngOnInit(): void {
        this._initSubject.next(true);
    }

    private shouldShowPlaceholder(channelReaches: ChannelReach[], selectedChannelId: number): boolean {
        return (!Array.isArray(channelReaches) || channelReaches.length === 0)
            || (!channelReaches.find(c => c.channelId === selectedChannelId))
            || (this.forPayingUser && this._userService.isFreeUser() && !this._userService.isAdmin);
    }

    private drawChannelReaches(channelReaches: ChannelReach[], selectedChannelId: number, otherIds: number[]): void {
        const drawnChannelReaches = this.getDrawnChannelReaches(channelReaches, selectedChannelId, this.otherSelectedChannelIds);
        this.channelReachItems = drawnChannelReaches.map(item => {
            const isSelected = item.channelId === selectedChannelId;

            return {
                ...item,
                chartData: [{
                    value: item.reachPercentage,
                    lineColor: isSelected ? this._selectedColor : this._unselectedColor
                }],
                reachChangePercentageDisplay: Math.abs(item.reachChangePercentage),
                isReachChangePositive: item.reachChangePercentage >= 0,
                uniqueCode: `${item.channelId}_${isSelected}`
            };
        });
    }

    private getDrawnChannelReaches(channelReaches: ChannelReach[], selectedChannelId: number, otherIds: number[]): ChannelReach[] {
        const results: ChannelReach[] = [];

        channelReaches.forEach(c => {
            const isDrawn = otherIds.indexOf(c.channelId) >= 0;

            if (isDrawn) {
                results.push(c);
            }
        });

        const selectedChannelReach = channelReaches.find(x => x.channelId === selectedChannelId);

        if (selectedChannelReach) {
            results.unshift(selectedChannelReach);
        }

        return results;
    }

    private calculateChannelSelector(channelReaches: ChannelReach[], selectedChannelId: number) {
        this.showChannelSelector = channelReaches.length > 1;

        if (!this.showChannelSelector) {
            this.channelSource = [];
            this.otherSelectedChannelIds = [];
            return;
        }

        const others = channelReaches.filter(x => x.channelId !== selectedChannelId);
        this.channelSource = others.map(x => {
            return {
                channelId: x.channelId,
                channelName: x.channelName,
                platformName: x.platformName,
            };
        });
        const sortedTop = others.sort((a, b) => b.reach - a.reach).slice(0, this._maxSelectedItemsCount);
        this.otherSelectedChannelIds = sortedTop.map(x => x.channelId);
    }

    public onSelectionChange(selectedChannels: ChannelItem[]) {
        if (selectedChannels.length > this._maxSelectedItemsCount) {
            this.otherSelectedChannelIds = [...this.otherSelectedChannelIds];
            this.exceedMaxChannelSelection.emit(null);
            return;
        }

        this.otherSelectedChannelIds = selectedChannels.map(x => x.channelId);
        this.drawChannelReaches(this._channelReaches, this._selectedChannelId, this.otherSelectedChannelIds);
    }

    public channelReachItemTrackBy(index: number, item: ChannelReachItem): string {
        return item.uniqueCode;
    }
}



// WEBPACK FOOTER //
// ./src/app/main/influencer/influencer-insight/channel-reach/channel-reach.component.ts